import type { QueravelTurn } from "@rhema/client";
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useLinks } from "../links/useLinks";
import { type CompletedTurn, QueravelRunner } from "./runner";
import { useActionHost } from "./useActionHost";

export {
  ACTION_RESULT_MESSAGE,
  type CompletedTurn,
  EXPIRY_CHECK_MS,
  type LiveTurn,
  type QueravelStatus,
} from "./runner";

/** Every Queravel conversation this app has open, kept above the screens so answers carry on. */
export class QueravelRunners {
  private runners = new Map<string, QueravelRunner>();
  private listeners = new Set<() => void>();
  private writing: string[] = [];

  constructor(private readonly make: (chatId: string) => QueravelRunner) {}

  get(chatId: string): QueravelRunner {
    let runner = this.runners.get(chatId);
    if (!runner) {
      runner = this.make(chatId);
      runner.subscribe(() => this.changed());
      this.runners.set(chatId, runner);
    }
    return runner;
  }

  // Listeners hear only when the set of conversations being written changes, not every word.
  private changed() {
    const writing = [...this.runners.values()].filter((r) => r.writing).map((r) => r.chatId);
    if (writing.join() === this.writing.join()) return;
    this.writing = writing;
    for (const l of this.listeners) l();
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Whether Queravel is writing an answer in any conversation. */
  isWriting = () => this.writing.length > 0;

  /** The conversations Queravel is writing an answer in. The same array until that changes. */
  writingIn = () => this.writing;

  /** The question being answered in a conversation, while one is. */
  question(chatId: string): string | null {
    const live = this.runners.get(chatId)?.state.live;
    return live ? (live.label ?? live.question ?? null) : null;
  }
}

const RunnersContext = createContext<QueravelRunners | null>(null);

/** Holds Queravel's conversations for the whole app. AppShell puts it above every screen. */
export function QueravelProvider({ children }: { children: ReactNode }) {
  const links = useLinks();
  const host = useActionHost();
  const runners = useMemo(
    () => new QueravelRunners((chatId) => new QueravelRunner(chatId, { links, host })),
    [links, host],
  );
  return createElement(RunnersContext.Provider, { value: runners }, children);
}

/** Runners from the provider, or a set of this screen's own where there is no provider. */
function useRunners(): QueravelRunners {
  const shared = useContext(RunnersContext);
  const links = useLinks();
  const host = useActionHost();
  const own = useRef<QueravelRunners | null>(null);
  if (shared) return shared;
  own.current ??= new QueravelRunners((chatId) => new QueravelRunner(chatId, { links, host }));
  return own.current;
}

const never = () => () => undefined;
const no = () => false;
const none: string[] = [];
const noneWriting = () => none;

/** Whether Queravel is writing an answer anywhere, for the marker in the chat list and the rail. */
export function useQueravelWriting(): boolean {
  const runners = useContext(RunnersContext);
  return useSyncExternalStore(
    runners?.subscribe ?? never,
    runners?.isWriting ?? no,
    runners?.isWriting ?? no,
  );
}

/**
 * The conversations Queravel is writing an answer in, each with the question it is answering, for
 * the marker on each row of the conversation list.
 */
export function useQueravelWritingIn(): { id: string; question: string | null }[] {
  const runners = useContext(RunnersContext);
  const ids = useSyncExternalStore(
    runners?.subscribe ?? never,
    runners?.writingIn ?? noneWriting,
    runners?.writingIn ?? noneWriting,
  );
  return useMemo(() => ids.map((id) => ({ id, question: runners?.question(id) ?? null })), [ids, runners]);
}

/**
 * One Queravel conversation, for the screen showing it. The answer is written by a runner that
 * outlives the screen: leave mid-answer and it carries on, and coming back shows it still writing
 * or finished. Reads that Queravel proposes run at once. Writes wait for the person.
 */
export function useQueravel({
  chatId,
  history,
  onComplete,
  onCancelled,
  model,
}: {
  /** The random id for this conversation. Never a Rhema chat id. */
  chatId: string;
  /** The turns so far, oldest first. Read when each request goes out. */
  history: () => QueravelTurn[];
  /** Called once an answer is complete, or stopped part way with some text. */
  onComplete: (turn: CompletedTurn) => void;
  /** Called when a question is stopped before any answer came. */
  onCancelled?: (question: string) => void;
  /** The model to answer with: the one picked on this device, or the plan default. */
  model?: string;
}) {
  const runner = useRunners().get(chatId);
  const callbacks = {
    history,
    onComplete,
    ...(onCancelled ? { onCancelled } : {}),
    model: () => model,
  };
  // The newest screen's callbacks win, and stay in place after it closes.
  runner.bind(callbacks);
  useEffect(() => {
    runner.bind(callbacks);
  });
  const state = useSyncExternalStore(runner.subscribe, runner.getState, runner.getState);
  return {
    status: state.status,
    error: state.error,
    live: state.live,
    proposals: state.proposals,
    queued: state.queued,
    ask: (message: string, label?: string, about?: string) => runner.ask(message, label, about),
    stop: runner.stop,
    retry: runner.retry,
    dismiss: runner.dismiss,
    confirm: (id: string) => void runner.engine.confirm(id),
    reject: (id: string) => runner.engine.reject(id),
    choose: (id: string, chat: string) => runner.engine.choose(id, chat),
  };
}

export type QueravelSession = ReturnType<typeof useQueravel>;
